// EPHEMERAL, ported from internal/wire/ephemeral.go.
//
// Attack flows and feed lines — the class the backpressure ladder of SPEC §3
// sheds first, because dropping it makes the map go quiet, not wrong.
//
// A pixel delta says what changed, never who did it. The §15 ballistic arcs need
// attribution, so attacks ride here, coalesced one flow per (attacker country,
// target pixel) per tick.
//
//	u8  tier
//	u16 flowCount
//	flowCount x (tier-specific record)
//	u16 lineCount
//	lineCount x (u8 kind | u16 country | u16 other | u8 hasPixel | u32 pixel | u16 textLen | text)
//
//	TierPixel  u32 srcPixelId | u32 dstPixelId | u16 attackerCountryId | u8 weight | u8 flags
//	TierChunk  u16 attackerCountryId | u16 srcChunkId | u16 dstChunkId | u16 weight
//
// Flows decode into parallel typed arrays because that is what the instanced arc
// draw call of §15 wants as vertex attributes — the renderer uploads these
// straight, with no repacking pass.
//
// Lines decode into objects: there are a handful of them per tick, each carries a
// string, and there is nothing to vectorise.

import { Reader, Writer, encodeUtf8 } from './bytes';
import { ClassEphemeral } from './classes';
import { validChunk, validPixel, type TickNo } from './core';
import {
  errBadBool,
  errBeachheadSource,
  errChunkID,
  errFlowTier,
  errLineText,
  errMissingSource,
  errPixelID,
  errTier,
  errTooManyRecords,
  errTruncated,
} from './errors';

export const TierPixel = 0;
export const TierChunk = 1;
export type Tier = typeof TierPixel | typeof TierChunk;

export function tierName(t: number): string {
  return t === TierChunk ? 'chunk' : 'pixel';
}

// --- flow flags (the arc's rendering bits, §3 and §15) --------------------

/** The shot that took the pixel: brighter flash, held longer, drops a capture marker. */
export const FlowKill = 1 << 0;
/** A shot at a fortress (§9), drawn hotter and thicker as its HP drops. */
export const FlowFortressSiege = 1 << 1;
/** A flow with no source pixel (§5). The only thing that says the source field is absent. */
export const FlowBeachhead = 1 << 2;
/** A shot produced by a national-treasury ability (§14) rather than an individual click. */
export const FlowAbility = 1 << 3;

/** Twelve bytes. */
export const PixelFlowSize = 4 + 4 + 2 + 1 + 1;
/** Eight bytes. */
export const ChunkFlowSize = 2 + 2 + 2 + 2;
/** Caps a feed line's UTF-8 body. */
export const MaxLineText = 512;

/** The smallest possible line: the fixed part with an empty body. */
const MinLineSize = 1 + 2 + 2 + 1 + 4 + 2;

// --- feed lines -----------------------------------------------------------

export const LineCapture = 0;
export const LineKill = 1;
export const LineFortressSiege = 2;
export const LineFortressFall = 3;
export const LineCountryEliminated = 4;
export const LineBeachhead = 5;
export const LineSeasonFreeze = 6;
export const LineAnnouncement = 7;

export interface Line {
  kind: number;
  country: number;
  other: number;
  pixel: number;
  hasPixel: boolean;
  text: string;
}

/**
 * Close-zoom arcs, in parallel arrays of length `count`.
 *
 * `hasSrc[i]` is 0 for a beachhead, whose source field is required to be zero on
 * the wire and is not a pixel id. Pixel 0 is the top-left corner of the board and
 * would read as a real source, which is exactly why the flag exists instead of a
 * sentinel.
 */
export interface PixelFlows {
  count: number;
  src: Uint32Array;
  hasSrc: Uint8Array;
  dst: Uint32Array;
  attacker: Uint16Array;
  /** Coalesced click count, saturating at 255. */
  weight: Uint8Array;
  flags: Uint8Array;
}

/** World-zoom arcs, aggregated over the ~2 s window between chunk centroids. */
export interface ChunkFlows {
  count: number;
  attacker: Uint16Array;
  src: Uint16Array;
  dst: Uint16Array;
  /** Saturates at 65535. */
  weight: Uint16Array;
}

export interface Ephemeral {
  tick: TickNo;
  tier: Tier;
  /** Populated only for TierPixel. */
  pixelFlows: PixelFlows | null;
  /** Populated only for TierChunk. */
  chunkFlows: ChunkFlows | null;
  lines: Line[];
}

export function decodeEphemeral(b: Uint8Array): Ephemeral {
  const r = new Reader(b);
  const tick = r.header(ClassEphemeral);
  const tier = r.u8();
  const n = r.u16();

  let pixelFlows: PixelFlows | null = null;
  let chunkFlows: ChunkFlows | null = null;

  if (tier === TierPixel) {
    if (n * PixelFlowSize > r.remaining) throw errTruncated();
    const f: PixelFlows = {
      count: n,
      src: new Uint32Array(n),
      hasSrc: new Uint8Array(n),
      dst: new Uint32Array(n),
      attacker: new Uint16Array(n),
      weight: new Uint8Array(n),
      flags: new Uint8Array(n),
    };
    for (let i = 0; i < n; i++) {
      const src = r.u32();
      const dst = r.u32();
      f.attacker[i] = r.u16();
      f.weight[i] = r.u8();
      const fl = r.u8();
      f.flags[i] = fl;
      if ((fl & FlowBeachhead) !== 0) {
        // The source field is not a pixel here. It must be zero, so that no
        // valid-looking id can be read out of it by mistake.
        if (src !== 0) throw errBeachheadSource();
      } else {
        if (!validPixel(src)) throw errPixelID();
        f.src[i] = src;
        f.hasSrc[i] = 1;
      }
      if (!validPixel(dst)) throw errPixelID();
      f.dst[i] = dst;
    }
    pixelFlows = f;
  } else if (tier === TierChunk) {
    if (n * ChunkFlowSize > r.remaining) throw errTruncated();
    const f: ChunkFlows = {
      count: n,
      attacker: new Uint16Array(n),
      src: new Uint16Array(n),
      dst: new Uint16Array(n),
      weight: new Uint16Array(n),
    };
    for (let i = 0; i < n; i++) {
      f.attacker[i] = r.u16();
      const src = r.u16();
      const dst = r.u16();
      f.weight[i] = r.u16();
      if (!validChunk(src) || !validChunk(dst)) throw errChunkID();
      f.src[i] = src;
      f.dst[i] = dst;
    }
    chunkFlows = f;
  } else {
    throw errTier();
  }

  const ln = r.u16();
  if (ln * MinLineSize > r.remaining) throw errTruncated();
  const lines: Line[] = new Array(ln);
  for (let i = 0; i < ln; i++) {
    const kind = r.u8();
    const country = r.u16();
    const other = r.u16();
    const has = r.u8();
    const px = r.u32();
    const tl = r.u16();
    if (has > 1) throw errBadBool();
    let pixel = 0;
    let hasPixel = false;
    if (has === 1) {
      if (!validPixel(px)) throw errPixelID();
      pixel = px;
      hasPixel = true;
    }
    if (tl > MaxLineText) throw errLineText();
    const text = r.text(tl);
    lines[i] = { kind, country, other, pixel, hasPixel, text };
  }
  r.done();
  return { tick, tier: tier as Tier, pixelFlows, chunkFlows, lines };
}

// --- encoding (server-side; here so tests can build frames) ---------------

export interface PixelFlowInput {
  src?: number;
  hasSrc: boolean;
  dst: number;
  attacker: number;
  weight: number;
  flags: number;
}

export interface ChunkFlowInput {
  attacker: number;
  src: number;
  dst: number;
  weight: number;
}

export interface EphemeralInput {
  tick: TickNo;
  tier: Tier;
  pixelFlows?: readonly PixelFlowInput[];
  chunkFlows?: readonly ChunkFlowInput[];
  lines?: readonly Line[];
}

/** Clamps a coalesced click count into the u8 weight field. */
export function saturateWeight8(n: number): number {
  return n < 0 ? 0 : n > 255 ? 255 : n | 0;
}

/** Clamps a coalesced click count into the u16 weight field. */
export function saturateWeight16(n: number): number {
  return n < 0 ? 0 : n > 0xffff ? 0xffff : n | 0;
}

export function encodeEphemeral(e: EphemeralInput): Uint8Array {
  const pf = e.pixelFlows ?? [];
  const cf = e.chunkFlows ?? [];
  const lines = e.lines ?? [];
  if (e.tier === TierPixel) {
    if (cf.length !== 0) throw errFlowTier();
    if (pf.length > 0xffff) throw errTooManyRecords();
  } else if (e.tier === TierChunk) {
    if (pf.length !== 0) throw errFlowTier();
    if (cf.length > 0xffff) throw errTooManyRecords();
  } else {
    throw errTier();
  }
  if (lines.length > 0xffff) throw errTooManyRecords();

  const w = new Writer(64);
  w.header(ClassEphemeral, e.tick).u8(e.tier);
  if (e.tier === TierPixel) {
    w.u16(pf.length);
    for (const f of pf) {
      const beach = (f.flags & FlowBeachhead) !== 0;
      if (beach && f.hasSrc) throw errBeachheadSource();
      if (!beach && !f.hasSrc) throw errMissingSource();
      if (!validPixel(f.dst) || (f.hasSrc && !validPixel(f.src ?? -1))) throw errPixelID();
      w.u32(f.hasSrc ? f.src! : 0)
        .u32(f.dst)
        .u16(f.attacker)
        .u8(f.weight)
        .u8(f.flags);
    }
  } else {
    w.u16(cf.length);
    for (const f of cf) {
      if (!validChunk(f.src) || !validChunk(f.dst)) throw errChunkID();
      w.u16(f.attacker).u16(f.src).u16(f.dst).u16(f.weight);
    }
  }

  w.u16(lines.length);
  for (const l of lines) {
    const body = encodeUtf8(l.text);
    if (body.byteLength > MaxLineText) throw errLineText();
    if (l.hasPixel && !validPixel(l.pixel)) throw errPixelID();
    w.u8(l.kind)
      .u16(l.country)
      .u16(l.other)
      .u8(l.hasPixel ? 1 : 0)
      .u32(l.hasPixel ? l.pixel : 0)
      .u16(body.byteLength)
      .bytes(body);
  }
  return w.finish();
}
